import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Label } from '../design-system';
import { login as loginApi, LoginRequest } from '../services/consentApi';
import { useReconsent } from '../hooks/useReconsent';
import ReconsentPopup from '../components/ReconsentPopup';

type LoginMode = 'slug' | 'tenantId';

interface LoginFormData {
  tenantSlug: string;
  tenantId: string;
  email: string;
  password: string;
}

/**
 * General login page - allows login with either tenant slug or tenant ID.
 * Route: /login
 * 
 * For users who:
 * - Forgot their tenant URL (/t/:tenantSlug/login)
 * - Know their tenant ID directly
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loginMode, setLoginMode] = useState<LoginMode>('slug');
  const [formData, setFormData] = useState<LoginFormData>({
    tenantSlug: '',
    tenantId: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [pendingUserInfo, setPendingUserInfo] = useState<any>(null);
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  // Re-consent hook
  const {
    requiresReconsent,
    consentStatus,
    policies,
    isLoading: reconsentLoading,
    error: reconsentError,
    checkReconsentNeeded,
    submitReconsent,
    dismissReconsent,
  } = useReconsent();

  const validationMessages = useMemo(
    () => ({
      tenantSlug: 'Organization slug is required',
      tenantId: 'Tenant ID is required',
      email: 'Email is required',
      password: 'Password is required',
    }),
    []
  );

  const validate = () => {
    const errors: string[] = [];
    if (loginMode === 'slug' && !formData.tenantSlug.trim()) {
      errors.push(validationMessages.tenantSlug);
    }
    if (loginMode === 'tenantId' && !formData.tenantId.trim()) {
      errors.push(validationMessages.tenantId);
    }
    if (!formData.email.trim()) errors.push(validationMessages.email);
    if (!formData.password.trim()) errors.push(validationMessages.password);
    setError(errors.join('\n'));
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const credentials: LoginRequest = {
        email: formData.email.trim(),
        password: formData.password,
      };
      
      // Use slug or tenantId based on mode
      if (loginMode === 'slug') {
        credentials.tenantSlug = formData.tenantSlug.trim();
      } else {
        credentials.tenantId = formData.tenantId.trim();
      }
      
      const data = await loginApi(credentials);
      
      const userInfo = data.user || {
        userId: data.userId,
        name: data.name,
        email: data.email,
        role: data.role,
        tenantId: data.tenantId,
        tenantSlug: data.tenantSlug,
      };

      // Get email for reconsent check - use formData as fallback
      const userEmail = userInfo.email || formData.email.trim();
      
      // Check if user needs to re-consent to platform policies
      const needsReconsent = await checkReconsentNeeded(userEmail);
      
      if (needsReconsent) {
        // Store credentials temporarily - don't fully login yet
        setPendingToken(data.token);
        setPendingUserInfo(userInfo);
        // The popup will be shown via requiresReconsent state
      } else {
        // No reconsent needed - complete login
        completeLogin(data.token, userInfo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Complete the login process after authentication (and reconsent if needed).
   */
  const completeLogin = (token: string, userInfo: any) => {
    localStorage.setItem('token', token);
    localStorage.setItem('authToken', token);
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    navigate('/admin/dashboard');
  };

  /**
   * Handle reconsent acceptance.
   */
  const handleReconsentAccept = async (privacyAccepted: boolean, termsAccepted: boolean) => {
    if (!pendingUserInfo) return;
    
    const success = await submitReconsent(pendingUserInfo.email, privacyAccepted, termsAccepted);
    
    if (success && pendingToken) {
      completeLogin(pendingToken, pendingUserInfo);
    }
  };

  /**
   * Handle logout from reconsent popup.
   */
  const handleReconsentLogout = () => {
    // Clear pending login state
    setPendingToken(null);
    setPendingUserInfo(null);
    dismissReconsent();
    setFormData(prev => ({ ...prev, password: '' }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <main className="ds-container" aria-labelledby="login-title">
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <Card>
          <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
            <div>
              <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
              <h1 id="login-title" className="ds-heading-xl">Sign in to your account</h1>
              <p className="ds-text-muted" style={{ marginTop: 6 }}>
                Enter your organization details and credentials to access your dashboard.
              </p>
            </div>
          </header>
          
          <form className="ds-form" onSubmit={handleSubmit} noValidate aria-describedby={error ? 'login-error' : undefined}>
            {error && (
              <div className="ds-alert ds-alert--error" id="login-error" role="alert">
                {error}
              </div>
            )}
            
            {/* Login mode toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                type="button"
                onClick={() => setLoginMode('slug')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid var(--ds-border)',
                  borderRadius: 'var(--ds-radius-md)',
                  background: loginMode === 'slug' ? 'var(--ds-primary)' : 'var(--ds-surface)',
                  color: loginMode === 'slug' ? 'var(--ds-primary-foreground)' : 'var(--ds-text-primary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                }}
              >
                Organization Slug
              </button>
              <button
                type="button"
                onClick={() => setLoginMode('tenantId')}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid var(--ds-border)',
                  borderRadius: 'var(--ds-radius-md)',
                  background: loginMode === 'tenantId' ? 'var(--ds-primary)' : 'var(--ds-surface)',
                  color: loginMode === 'tenantId' ? 'var(--ds-primary-foreground)' : 'var(--ds-text-primary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                }}
              >
                Tenant ID
              </button>
            </div>

            {loginMode === 'slug' ? (
              <div className="ds-field">
                <Label htmlFor="tenantSlug">Organization Slug</Label>
                <Input
                  type="text"
                  id="tenantSlug"
                  name="tenantSlug"
                  value={formData.tenantSlug}
                  onChange={handleChange}
                  placeholder="e.g., acme-corp-x7k9"
                  required
                  autoComplete="organization"
                />
                <span className="ds-text-muted" style={{ fontSize: 12 }}>
                  The slug from your login URL: /t/<strong>your-slug</strong>/login
                </span>
              </div>
            ) : (
              <div className="ds-field">
                <Label htmlFor="tenantId">Tenant ID</Label>
                <Input
                  type="text"
                  id="tenantId"
                  name="tenantId"
                  value={formData.tenantId}
                  onChange={handleChange}
                  placeholder="Enter your tenant ID (UUID)"
                  required
                  autoComplete="organization"
                />
              </div>
            )}

            <div className="ds-field">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="ds-field">
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--ds-accent)' }}>
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <footer className="ds-card__footer">
            <p className="ds-text-muted" style={{ margin: 0, textAlign: 'center' }}>
              Want to use the Consent Platform for your organization?{' '}
              <Link to="/signup" style={{ color: 'var(--ds-accent)' }}>
                Request access
              </Link>
            </p>
          </footer>
        </Card>
      </div>

      {/* Re-consent Popup - shown when user needs to accept updated policies */}
      {requiresReconsent && (
        <ReconsentPopup
          policies={policies}
          consentStatus={consentStatus}
          isLoading={reconsentLoading}
          error={reconsentError}
          onAccept={handleReconsentAccept}
          onLogout={handleReconsentLogout}
        />
      )}
    </main>
  );
};

export default Login;
